import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Picker from 'emoji-picker-react';
import type { EmojiClickData, Theme } from 'emoji-picker-react';

interface EmojiPickerProps {
  onEmojiSelect: (emoji: string) => void;
  buttonClassName?: string;
  buttonLabel?: string;
  isOpen?: boolean; // Allow external control
  hideButton?: boolean; // Hide button for external control use cases
}

export const EmojiPicker: React.FC<EmojiPickerProps> = ({
  onEmojiSelect,
  buttonClassName = 'p-1 text-muted hover:text-text transition-colors',
  buttonLabel = 'Add emoji',
  isOpen: externalIsOpen,
  hideButton = false,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [inlinePopoverStyle, setInlinePopoverStyle] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [inlinePickerWidth, setInlinePickerWidth] = useState(320);
  const [inlinePickerHeight, setInlinePickerHeight] = useState(400);
  const [inlinePlacement, setInlinePlacement] = useState<'top' | 'bottom'>('top');
  const triggerRef = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);

  // Use external control if provided, otherwise use internal state
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;

  // Close picker when clicking outside (only for internal control)
  useEffect(() => {
    if (!isOpen || externalIsOpen !== undefined) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedInsidePicker = pickerRef.current?.contains(target);
      const clickedTrigger = triggerRef.current?.contains(target);
      if (!clickedInsidePicker && !clickedTrigger) {
        setInternalIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, externalIsOpen]);

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    onEmojiSelect(emojiData.emoji);
    if (externalIsOpen === undefined) {
      setInternalIsOpen(false);
    }
  };

  // Detect theme from document (default to 'auto' if not detectable)
  const theme: Theme = (document.documentElement.classList.contains('dark') ? 'dark' : document.documentElement.classList.contains('light') ? 'light' : 'auto') as Theme;

  const updateInlinePopoverPosition = useCallback(() => {
    if (!triggerRef.current) return;

    const DEFAULT_WIDTH = 320;
    const DEFAULT_HEIGHT = 400;
    const MIN_HEIGHT = 260;
    const VIEWPORT_PADDING = 12;
    const GAP = 8;

    const rect = triggerRef.current.getBoundingClientRect();
    const chatDrawer = document.querySelector('[data-chat-drawer="true"]') as HTMLElement | null;
    const drawerRect = chatDrawer?.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const rightBoundary = drawerRect ? drawerRect.right - VIEWPORT_PADDING : viewportWidth - VIEWPORT_PADDING;
    const leftBoundary = drawerRect ? drawerRect.left + VIEWPORT_PADDING : VIEWPORT_PADDING;
    const maxAllowedWidth = Math.max(280, rightBoundary - leftBoundary);
    const width = Math.min(DEFAULT_WIDTH, maxAllowedWidth);
    const availableAbove = rect.top - GAP - VIEWPORT_PADDING;
    const availableBelow = viewportHeight - rect.bottom - GAP - VIEWPORT_PADDING;

    const placeAbove = availableAbove >= DEFAULT_HEIGHT || availableAbove >= availableBelow;
    const chosenAvailable = placeAbove ? availableAbove : availableBelow;
    const height = Math.max(MIN_HEIGHT, Math.min(DEFAULT_HEIGHT, chosenAvailable));

    const idealLeft = rect.left + rect.width / 2 - width / 2;
    let left = idealLeft;
    if (left + width > rightBoundary) {
      left = rightBoundary - width;
    }
    if (left < leftBoundary) {
      left = leftBoundary;
    }

    const top = placeAbove ? rect.top - height - GAP : rect.bottom + GAP;

    setInlinePickerWidth(width);
    setInlinePickerHeight(height);
    setInlinePlacement(placeAbove ? 'top' : 'bottom');
    setInlinePopoverStyle({ top, left });
  }, []);

  useEffect(() => {
    if (!isOpen || hideButton) return;

    updateInlinePopoverPosition();

    const handleWindowChange = () => updateInlinePopoverPosition();
    window.addEventListener('resize', handleWindowChange);
    window.addEventListener('scroll', handleWindowChange, true);

    return () => {
      window.removeEventListener('resize', handleWindowChange);
      window.removeEventListener('scroll', handleWindowChange, true);
    };
  }, [isOpen, hideButton, updateInlinePopoverPosition]);

  // When hideButton is true, use a portal to render at document root (avoid z-index stacking issues)
  if (hideButton && isOpen) {
    return createPortal(
      <div 
        ref={pickerRef}
        className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none"
      >
        <div className="bg-surface rounded-lg shadow-2xl border border-border pointer-events-auto">
          <Picker
            onEmojiClick={handleEmojiClick}
            theme={theme}
            searchPlaceHolder="Search emoji..."
            skinTonesDisabled={false}
            height={400}
            width={320}
            previewConfig={{ showPreview: false }}
          />
        </div>
      </div>,
      document.body
    );
  }

  // Standard button + picker mode
  return (
    <div className="relative" ref={triggerRef}>
      <button
        type="button"
        onClick={() => setInternalIsOpen(!internalIsOpen)}
        className={buttonClassName}
        aria-label={buttonLabel}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </button>

      {isOpen && createPortal(
        <div
          ref={pickerRef}
          className="fixed z-[100]"
          style={{ top: inlinePopoverStyle.top, left: inlinePopoverStyle.left }}
          data-placement={inlinePlacement}
        >
          <div className="relative rounded-lg shadow-2xl ring-1 ring-black/20">
            <span
              className={`absolute left-1/2 -translate-x-1/2 w-2.5 h-2.5 rotate-45 bg-[#222] border border-border ${
                inlinePlacement === 'top' ? 'bottom-[-6px] border-l-0 border-t-0' : 'top-[-6px] border-r-0 border-b-0'
              }`}
              aria-hidden="true"
            />
            <Picker
              onEmojiClick={handleEmojiClick}
              theme={theme}
              searchPlaceHolder="Search emoji..."
              skinTonesDisabled={false}
              height={inlinePickerHeight}
              width={inlinePickerWidth}
              previewConfig={{ showPreview: false }}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
